import React from "react";
import { Card, Tag, Button, Space, Grid } from "antd";
import { LoginOutlined } from "@ant-design/icons";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import MembershipCardGenerator from "../../MembershipCardGenerator";
import PosTransactionModal from "../PosTransactionModal";
import { MemberProfileMode } from "./modeConfig";
import { statusMeta } from "./validity";
import ValidityMeter from "./ValidityMeter";

interface IdentityHeaderProps {
    member: MemberProfile;
    mode: MemberProfileMode;
    // Hide the membership ID when a member is viewing their own profile — it's an
    // internal reference they don't need.
    selfServiceMode?: boolean;
    onCheckIn: () => void;
    checkingIn: boolean;
    checkInDisabled: boolean;
    // Fired after a sale is taken from the banner's Point of Sale action, so the
    // Transactions tab can refresh. The same action also sits in the quick-actions
    // rail — staff reach for whichever is nearer, and both open the same till.
    onPosTransactionUpdated: () => void;
}

// Compact full-width identity bar: name, status, key facts, the validity meter
// and the primary actions. The photo lives in its own left-rail card
// (MemberPhotoCard) so it doesn't stretch this row. Wraps to a stack on mobile.
const IdentityHeader: React.FC<IdentityHeaderProps> = ({ member, mode, selfServiceMode = false, onCheckIn, checkingIn, checkInDisabled, onPosTransactionUpdated }) => {
    const screens = Grid.useBreakpoint();
    const compact = screens.lg === false;
    const sm = statusMeta(member);

    const actions = (
        <Space wrap size={8} style={{ justifyContent: compact ? "flex-start" : "flex-end" }}>
            {mode.checkInButton && (
                <Button type="primary" icon={<LoginOutlined />} loading={checkingIn} disabled={checkInDisabled} onClick={onCheckIn}>
                    Check in
                </Button>
            )}
            {mode.actions && (
                <PosTransactionModal
                    member={member}
                    onTransactionCompleted={onPosTransactionUpdated}
                    triggerTestId="pos-header-trigger"
                />
            )}
            {mode.actions && <MembershipCardGenerator member={member} />}
        </Space>
    );

    return (
        <Card className="ft-id-accent" data-testid="profile-identity-header" styles={{ body: { padding: compact ? 16 : 18 } }} style={{ borderRadius: 14 }}>
            <div style={{ display: "flex", gap: 20, alignItems: compact ? "flex-start" : "center", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 240px", minWidth: 200 }}>
                    <div className="ft-id-name">{member.name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                        <Tag color={sm.color} style={{ marginInlineEnd: 0, fontWeight: 700 }}>{sm.text}</Tag>
                        {!selfServiceMode && (
                            <>
                                <span className="ft-id-sub" style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>{member.membershipId}</span>
                                <span style={{ color: "var(--ft-line)" }}>•</span>
                            </>
                        )}
                        <span className="ft-id-sub">{member.membershipCategory?.name}</span>
                        {member.memberType && member.memberType !== "Member" && (
                            <Tag bordered={false} color="purple" style={{ marginInlineEnd: 0 }} data-testid="member-type-tag">{member.memberType}</Tag>
                        )}
                        {member.specialOffers && (
                            <Tag bordered={false} color="processing" style={{ marginInlineEnd: 0 }}>Offers opted-in</Tag>
                        )}
                    </div>
                </div>

                {!compact && <ValidityMeter member={member} style={{ minWidth: 200 }} />}
                <div style={{ flex: compact ? "1 1 100%" : "0 0 auto" }}>{actions}</div>
            </div>
            {compact && <ValidityMeter member={member} style={{ marginTop: 14 }} />}
        </Card>
    );
};

export default IdentityHeader;
